import { useState, useEffect, useCallback } from "react";
import { Box, Typography, Paper } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import axios from "axios";
import CustomTableWithPagination from "../../../components/customTable/CustomTable";
import { getFormattedDate } from "../../../utils/commonFunctions";

interface UserDevicesTabProps {
  userId: string;
  refreshSignal?: number;
}

const apiEndpoint: any = process.env.REACT_APP_API_BASE_URL;

interface LastPingData {
  PushNotificationId: string;
  UserId: string;
  FirstSeen: string;
  LastLat: number;
  LastSeen: string;
  DeviceId: string;
  LastLong: number;
}

interface DeviceData {
  IsNotificationAllowed: boolean;
  UserId: string;
  CreationDate: string;
  AppVersion: string;
  IsLocationEnabled: boolean;
  LastModifiedDate: string;
  NetworkProvider: string;
  DeviceOSVersion: string;
  DeviceId: string;
  Model: string;
  DevicePlatform: string;
}

const UserDevicesTab = ({ userId, refreshSignal }: UserDevicesTabProps) => {
  const [loading, setLoading] = useState(false);
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastPingData, setLastPingData] = useState<LastPingData[]>([]);
  const [devices, setDevices] = useState<DeviceData[]>([]);

  const fetchLastPing = useCallback(() => {
    if (!userId) return;
    setLoading(true);
    setError(null);
    axios
      .get(`${apiEndpoint}/userdevices/support/ping/${userId}`, {
        headers: {
          Authorization: `bearer ${localStorage.getItem("accessToken")}`,
          "Content-Type": "application/json"
        }
      })
      .then((res) => setLastPingData(res.data || []))
      .catch(() => setError("Failed to fetch last ping data"))
      .finally(() => setLoading(false));
  }, [userId]);

  const fetchDevices = useCallback(() => {
    if (!userId) return;
    setDevicesLoading(true);
    setError(null);
    axios
      .get(`${apiEndpoint}/userdevices/support/devices/${userId}`, {
        headers: {
          Authorization: `bearer ${localStorage.getItem("accessToken")}`,
          "Content-Type": "application/json"
        }
      })
      .then((res) => setDevices(res.data || []))
      .catch(() => setError("Failed to fetch devices"))
      .finally(() => setDevicesLoading(false));
  }, [userId]);

  const handleRefresh = () => {
    fetchLastPing();
    fetchDevices();
  };

  useEffect(() => {
    fetchLastPing();
    fetchDevices();
  }, [userId, fetchLastPing, fetchDevices, refreshSignal]);

  // Define columns for the last ping table
  const lastPingColumns = [
    {
      id: "DeviceId",
      label: "Device ID",
      minWidth: 200,
      align: "left",
      format: (value: any) => value ?? "-"
    },
    {
      id: "LastSeen",
      label: "Last Seen",
      minWidth: 180,
      align: "left",
      format: (value: any) => (value ? getFormattedDate(value) : "-")
    },
    {
      id: "LastLat",
      label: "Last Latitude",
      minWidth: 150,
      align: "left",
      format: (value: any) => value ?? "-"
    },
    {
      id: "LastLong",
      label: "Last Longitude",
      minWidth: 150,
      align: "left",
      format: (value: any) => value ?? "-"
    }
  ];

  // Define columns for the devices table
  const devicesColumns = [
    {
      id: "DeviceId",
      label: "Device ID",
      minWidth: 200,
      align: "left",
      format: (value: any) => value ?? "-"
    },
    {
      id: "AppVersion",
      label: "App Version",
      minWidth: 150,
      align: "left",
      format: (value: any) => value ?? "-"
    },
    {
      id: "DevicePlatform",
      label: "Platform",
      minWidth: 120,
      align: "left",
      format: (value: any) => value ?? "-"
    },
    {
      id: "Model",
      label: "Model",
      minWidth: 150,
      align: "left",
      format: (value: any) => value ?? "-"
    },
    {
      id: "DeviceOSVersion",
      label: "OS Version",
      minWidth: 120,
      align: "left",
      format: (value: any) => value ?? "-"
    },
    {
      id: "NetworkProvider",
      label: "Network Provider",
      minWidth: 150,
      align: "left",
      format: (value: any) => value ?? "-"
    },
    {
      id: "CreationDate",
      label: "Creation Date",
      minWidth: 180,
      align: "left",
      format: (value: any) => (value ? getFormattedDate(value) : "-")
    }
  ];

  return (
    <Box>
      <div style={{ float: "right", cursor: "pointer" }}>
        <RefreshIcon onClick={handleRefresh} />
      </div>

      {/* Last Ping Details Table */}
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
        Last Ping Details
      </Typography>
      <CustomTableWithPagination
        columns={lastPingColumns}
        data={lastPingData}
        apiStatus={loading ? "fetching" : "idle"}
        payloadSize={100}
        currentPage={1}
        totalCount={lastPingData.length}
        lastEvaluatedKey={null}
        handleChangeRowsPerPage={() => {}}
        handleChangePage={() => {}}
        isShowPagination={false}
        noDataFoundMessage="No last ping data found."
        sx={{ mb: 4 }}
      />

      {/* Devices Table */}
      <Typography variant="h6" sx={{ mb: 2, mt: 4, fontWeight: 600 }}>
        All Devices
      </Typography>
      <CustomTableWithPagination
        columns={devicesColumns}
        data={devices}
        apiStatus={devicesLoading ? "fetching" : "idle"}
        payloadSize={100}
        currentPage={1}
        totalCount={devices.length}
        lastEvaluatedKey={null}
        handleChangeRowsPerPage={() => {}}
        handleChangePage={() => {}}
        isShowPagination={false}
        noDataFoundMessage="No devices found."
      />
    </Box>
  );
};

export default UserDevicesTab;
